import {useAppBridge} from '@shopify/app-bridge-react';
import {
    LegacyCard,
    DescriptionList,
    Layout,
    ResourceList,
    ResourceItem,
    Frame,
    Page,
    Pagination,
    EmptyState,
    Badge,
    Button,
    LegacyStack,
    Spinner,
    TextField,
    Icon,
    Form,
    ButtonGroup,
    Modal,
    Banner,
    Text,
    Combobox,
    Listbox,
    Tag,
    InlineStack,
    AutoSelection,
    FormLayout,
} from "@shopify/polaris";
import {useCallback, useEffect} from 'react';
import { useState } from 'react';
import { useHistory, useParams, useLocation } from 'react-router-dom';
import Api from '../../helpers/api';
import CustomerCreditCard from './CustomerCreditCard';
import CustomerOnHold from "./CustomerOnHold";
import {str_slug, getFormError} from "../../helpers/utils";
import {SearchIcon} from "@shopify/polaris-icons";
import {CustomerOrders} from "./CustomerOrders";

const Paginator = ({ collection = [], meta, callback, customerId}) => {
    if (! collection.length) {
        return null;
    }

    return (
        <LegacyCard.Section>
            <Pagination
                label={`Showing Page ${meta.current_page} of ${meta.last_page}`}
                hasPrevious={ meta.current_page > 1}
                onPrevious={() => { callback(customerId, meta.current_page - 1) }}
                hasNext={ meta.current_page < meta.last_page }
                onNext={() => { callback(meta.current_page + 1) }} />
        </LegacyCard.Section>
    );
}

const LocationsPaginator = ({ locations = [], locationsMeta, getLocations }) => {
    if (! locations.length) {
        return null;
    }

    return (
        <LegacyCard.Section>
            <Pagination
                label={`Showing Page ${locationsMeta.current_page} of ${locationsMeta.last_page}`}
                hasPrevious={ locationsMeta.current_page > 1}
                onPrevious={() => { getLocations(locationsMeta.current_page - 1) }}
                hasNext={ locationsMeta.current_page < locationsMeta.last_page }
                onNext={() => { getLocations(locationsMeta.current_page + 1) }} />
        </LegacyCard.Section>
    );
}

const AddressBlock = ({ location, label }) => location
    ? (
        <div>
            <h3><Text  variant="bodyMd" as="span" fontWeight="semibold">{ label }</Text></h3>
            {location.company && <>{location.company}<br /></>}
            {location.address1}<br />
            {location.address2 && <>{location.address2}<br /></>}
            {location.city}, {location.state} {location.zip_code}
            <p>
                { location.type && <div><Text  variant="bodyMd" as="span" fontWeight="semibold">Type:</Text> {location.type || 'Not Defined'}</div>}
            </p>
        </div>
    )
    : null;

const LocationsEmptyState = ({ locations }) => {
    if (locations.length) {
        return null;
    }

    return (<EmptyState heading="The customer has no additional locations"></EmptyState>)
}

const OnHoldToggle = ({ customer, setCustomer }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState({can_manage_on_hold: false, is_admin: false})
    const [hasOrders, setHasOrders] = useState(true);
    const history = useHistory();

    useEffect(() => {
        setLoading(true);

        Promise
            .all([

                Api.hasCustomerOrders(customer.id),
                Api.getMe()
            ])
            .then(results => {
                setHasOrders(results[0].data.status ?? false)
                setUser(results[1].data.user ?? {can_manage_on_hold: false, is_admin: false})
            })
            .finally(() => setLoading(false));
    }, [customer.id]);

    const toggleOnHold = useCallback(async () => {
        if (loading)
            return;

        setLoading(true);
        const response = await Api.toggleCustomerOnHold(customer.id);
        setCustomer(response.data.data);
        setLoading(false);
    }, [customer, loading]);

    const handleDelete = useCallback(() => {
        if (loading) {
            return;
        }

        setLoading(true);
        const confirmation = confirm("Are you sure you want to delete this customer?");

        if (confirmation) {
            Api.deleteCustomer(customer.id).then(() => {
                history.goBack();
                setLoading(false);
            });
        } else {
            setLoading(false);
        }
    }, [loading, customer]);

    return (
        <LegacyCard sectioned>
            <ButtonGroup>
                {!hasOrders && (
                    <Button variant="primary" tone="critical" onClick={handleDelete} loading={loading}>
                        Delete customer
                    </Button>
                )}
                <Button variant="primary" tone="critical" onClick={toggleOnHold} loading={loading} disabled={loading || (!user.can_manage_on_hold && !user.is_admin)}>
                    {customer?.is_on_hold ? 'Unset from hold' : 'Set on hold'}
                </Button>
            </ButtonGroup>
        </LegacyCard>
    );
}

const ChangeStatus = ({ customer, setCustomer }) => {
    if (customer.status === 'approved') {
        return null;
    }

    return <>
        <LegacyCard sectioned>
            {customer.default_location?.shipping && <AddressBlock
                label={'Shipping Address'}
                location={customer.default_location.shipping}
            />}
            <br />
            <hr />
            <br />
            {customer.default_location?.billing && <AddressBlock
                label={'Billing Address'}
                location={customer.default_location.billing}
            />}
        </LegacyCard>
    </>;
}

const LocationsCard = ({ customer, placeOrder }) => {
    if (! customer.id) {
        return null;
    }

    const history = useHistory();
    const [locations, setLocations] = useState([]);
    const [locationsMeta, setLocationsMeta] = useState({});
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);

    const getLocations = useCallback((page = 1, query = null) => {
        let searchParam = query ?? search;

        if (searchParam && searchParam.length > 0 && searchParam.length < 3) {
            alert("You need to type at least 3 characters");
            return;
        }

        setLoading(true);
        Api
            .getLocations(customer.id, page, searchParam)
            .then(({ data }) => {
                setLocations(data.data);
                setLocationsMeta(data.meta);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [locations, locationsMeta, loading, search, customer.id])

    useEffect(() => {
        getLocations(1);
    }, [customer.id]);

    return (
        <LegacyCard
            title="Locations"
            sectioned>
            <LegacyCard.Section>
                <Form
                    onSubmit={() => {
                        getLocations(1);
                    }}
                >
                    <TextField
                        placeholder={"Search locations"}
                        value={search}
                        onChange={setSearch}
                        clearButton
                        onClearButtonClick={() => {
                            setSearch('');
                            getLocations(1, '');
                        }}
                        connectedRight={(
                            <Button
                                primary
                                onClick={() => {
                                    getLocations(1)
                                }}
                            >
                                <Icon source={SearchIcon} />
                            </Button>
                        )}
                    />
                </Form>
            </LegacyCard.Section>
            <LocationsPaginator locations={locations}
                                locationsMeta={locationsMeta}
                                getLocations={getLocations} />
            <LegacyCard.Section>
                { loading && (
                    <Spinner size={"small"} />
                )}
                { !loading && (
                    <ResourceList
                        resourceName={{singular: 'Location', plural: 'Locations'}}
                        items={locations}
                        emptyState={<LocationsEmptyState locations={locations} />}
                        renderItem={(location) => {
                            const shortcutActions = [
                                {
                                    content: 'Place Order',
                                    accessibilityLabel: `Place order for location ${location.id}`,
                                    onAction: () => { placeOrder(location.id); }
                                }
                            ]

                            const addresses = [
                                {location: location.billing, label: 'Billing Address'},
                                {location: location.shipping, label: 'Shipping Address'},
                            ].filter(item => item.location);

                            const badges = [
                                location.default && <Badge size='small'>Default</Badge>,
                                location.shopify_customer_id && <Badge size='small'>Shopify Customer</Badge>,
                            ].filter(Boolean);

                            return (
                                <ResourceItem onClick={ () => { history.push(`/locations/${location.id}`); }} shortcutActions={shortcutActions}>
                                    <LegacyStack vertical>
                                        <div>{badges}</div>
                                        { location.phone && <div><Text  variant="bodyMd" as="span" fontWeight="semibold">Phone:</Text> {location.phone}</div> }
                                        <LegacyStack>
                                            {addresses.map((item, i) => (<AddressBlock {...item} key={`location-${i}`} />))}
                                        </LegacyStack>
                                        <div>
                                            <h3><Text  variant="bodyMd" as="span" fontWeight="semibold">Notes</Text></h3>
                                            {location.notes}
                                        </div>
                                    </LegacyStack>
                                </ResourceItem>
                            );
                        }}
                    />
                )}
            </LegacyCard.Section>
            <LocationsPaginator locations={locations}
                                locationsMeta={locationsMeta}
                                getLocations={getLocations} />
        </LegacyCard>
    );
}

const Invoices = () => {
    const [invoices, setInvoices] = useState([]);
    const [invoicesMeta, setInvoicesMeta] = useState({});
    const [isLoadingInvoices, setIsLoadingInvoices] = useState(true);
    const {customerId} = useParams();

    useEffect(() => {
        getInvoices(1 );
    }, [customerId]);

    const getInvoices = (page = 1) => {
        setIsLoadingInvoices(true);
        Api.getInvoices( customerId, page )
            .then( ({data}) => {
                setInvoices( data.data );
                setInvoicesMeta( data.meta );
            })
            .finally(() => setIsLoadingInvoices(false));
    }

    const downloadFile = function (data, title){
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${title}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
    };

    const downloadInvoice = useCallback((id, orderNumber) => {
        setIsLoadingInvoices(true);
        Api.downloadInvoice(customerId, id)
            .then((response) => downloadFile(response.data, str_slug(orderNumber)))
            .finally(() => setIsLoadingInvoices(false))
    }, [customerId])

    return (
        <div style={{
            position: "relative",
            marginTop: "20px"
        }}>
            <LegacyCard sectioned title="Invoices">
                { isLoadingInvoices && (<div style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    background: "#00000040",
                    zIndex: 1000,
                    textAlign: "center",
                    paddingTop: "10px"
                }}><Spinner size={"large"} /></div>) }
                <ResourceList
                    resourceName={{singular: 'Invoice', plural: 'Invoices'}}
                    items={invoices}
                    emptyState={<div>This customer does not have any invoices</div>}
                    renderItem={(invoice) => {
                        return (
                            <ResourceItem
                                onClick={ () => { downloadInvoice(invoice.id, invoice.order_number) }}
                            >
                                <div>Invoice <strong>{invoice.order_number}</strong></div>
                            </ResourceItem>
                        )
                    }}
                />
                <Paginator collection={invoices}
                           meta={invoicesMeta}
                           callback={getInvoices}
                           customerId={customerId}
                />
            </LegacyCard>
        </div>
    );
}

const MergeModal = ({ show, customer, onClose }) => {
    const [accountNumber, setAccountNumber] = useState('');
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const shopify = useAppBridge();
    const updateAccountNumber = useCallback(value => setAccountNumber(value));
    const history = useHistory();

    useEffect(() => {
        setAccountNumber('')
        setErrors({});
        setLoading(false);
    }, [show]);

    const onMerge = useCallback(() => {
        setLoading(true);
        setErrors({});

        Api.mergeAccount(customer.id, accountNumber)
            .then(response => {
                shopify.toast.show('Account was merged successfully');
                history.push(`/customers/${response.data.customer_id}`);
            })
            .catch(error => {
                if (error.response.status == 422) {
                    setErrors(error.response.data.errors);
                } else {
                    shopify.toast.show(getErrorMessage(error), {
                        isError: true,
                    });
                }

                setLoading(false);
            });
    }, [customer.id, accountNumber]);

    return (
        <Modal open={show}
               title="Merge Account"
               onClose={onClose}
               secondaryActions={[
                   { content: 'Cancel', onAction: onClose }
               ]}
               primaryAction={{
                   content: 'Merge',
                   loading,
                   onAction: onMerge,
               }}>
            <Modal.Section>
                <LegacyStack vertical>
                    <LegacyStack.Item>
                        <TextField label="Account Number"
                                   type="text"
                                   value={accountNumber}
                                   onChange={updateAccountNumber}
                                   error={getFormError(errors, 'with_account')} />
                    </LegacyStack.Item>
                    <LegacyStack.Item>
                        <Banner tone="info">After merging the customer accounts, the associated locations will be moved to the other account, and the this customer account will be hidden.</Banner>
                    </LegacyStack.Item>
                </LegacyStack>
            </Modal.Section>
        </Modal>
    );
}

const EmailsSection = ({ customer, setCustomer }) => {
    const [secondaryEmails, setSecondaryEmails] = useState(customer.secondary_emails || []);
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [searching, setSearching] = useState(false);
    const [saving, setSaving] = useState(false);
    const shopify = useAppBridge();

    const handleInputChange = useCallback(async (value) => {
        setInputValue(value);
        if (value.length < 2) {
            setOptions([]);
            return;
        }
        setSearching(true);
        try {
            const response = await Api.findCustomer(value);
            const customers = response.data.data || [];
            setOptions(
                customers
                    .filter(c => c.email && c.id !== customer.id)
                    .map(c => ({
                        label: `${c.email}${c.account_number ? ' (' + c.account_number + ')' : ''}`,
                        value: c.email,
                    }))
            );
        } catch {
            setOptions([]);
        } finally {
            setSearching(false);
        }
    }, [customer.id]);

    const handleSelect = useCallback((selected) => {
        if (!secondaryEmails.includes(selected)) {
            setSecondaryEmails(prev => [...prev, selected]);
        }
        setInputValue('');
        setOptions([]);
    }, [secondaryEmails]);

    const removeEmail = useCallback((email) => () => {
        setSecondaryEmails(prev => prev.filter(e => e !== email));
    }, []);

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const response = await Api.updateCustomerEmails(customer.id, { secondary_emails: secondaryEmails });
            setCustomer(response.data.data);
            shopify.toast.show('Emails updated successfully');
        } catch {
            shopify.toast.show('Failed to update emails', { isError: true });
        } finally {
            setSaving(false);
        }
    }, [customer.id, secondaryEmails]);

    return (
        <LegacyCard sectioned title="Email Settings">
            <FormLayout>
                <TextField
                    label="Main Email"
                    value={customer.email || ''}
                    disabled
                    autoComplete="off"
                />
                <div>
                    <div style={{ marginBottom: '0.25rem' }}>
                        <Text as="span" variant="bodyMd">Secondary Emails</Text>
                    </div>
                    <Combobox
                        allowMultiple
                        activator={
                            <Combobox.TextField
                                label="Secondary Emails"
                                labelHidden
                                value={inputValue}
                                onChange={handleInputChange}
                                placeholder="Search customers by email..."
                                autoComplete="off"
                                loading={searching}
                            />
                        }
                    >
                        {options.length > 0 && (
                            <Listbox
                                autoSelection={AutoSelection.None}
                                onSelect={handleSelect}
                            >
                                {options.map(option => (
                                    <Listbox.Option
                                        key={option.value}
                                        value={option.value}
                                        selected={secondaryEmails.includes(option.value)}
                                        accessibilityLabel={option.label}
                                    >
                                        {option.label}
                                    </Listbox.Option>
                                ))}
                            </Listbox>
                        )}
                    </Combobox>
                    {secondaryEmails.length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                            <InlineStack gap="200">
                                {secondaryEmails.map(email => (
                                    <Tag key={email} onRemove={removeEmail(email)}>
                                        {email}
                                    </Tag>
                                ))}
                            </InlineStack>
                        </div>
                    )}
                </div>
                <Button variant="primary" onClick={handleSave} loading={saving}>
                    Save Email Settings
                </Button>
            </FormLayout>
        </LegacyCard>
    );
};

const ShowCustomer = () => {
    const history = useHistory();
    const [customer, setCustomer] = useState(null);
    const {customerId} = useParams();
    const {search} = useLocation();
    const [backUrl] = useState(
        (new URLSearchParams(search)).get('back') || null
    );
    const [showMergeModal, setShowMergeModal] = useState(false);
    const closeMergeModal = useCallback(() => setShowMergeModal(false), [showMergeModal]);

    const placeOrder = (location_id) => {
        history.push(`/orders/${customer.id}?location_id=${location_id}`);
    }

    useEffect(() => {
        getCustomer(customerId);
        setShowMergeModal(false);
    }, [customerId]);

    const getCustomer = (customerId) => {
        Api.getCustomer(customerId).then( response => {
            setCustomer(response.data.data);
        });
        Api.addToRegistry('customer_view', customerId);
    }

    const editCustomer = () => {
        history.push(`/customers/${customerId}/edit`);
    }

    const onCustomerUpdate = useCallback((data) => {
        setCustomer(data.customer);
    });

    const primaryFooterAction = customer?.default_location
        ? { content: 'Place Order', onAction: () => { placeOrder(customer.default_location.id) } }
        : null;

    const secondaryActions = [
        {
            content: 'Merge',
            onAction: () => setShowMergeModal(true),
        }
    ];

    const primaryAction = {
        content: 'Edit Customer',
        onAction: editCustomer,
    };

    const shippingMethod = customer?.shipping_method?.name
        ? `${customer.shipping_method.name} - $${customer.shipping_method.price}`
        : null;

    const getCustomerFinancialInformation = () => {
        const data = [
            { term: 'Total Unpaid', 'description': `$${(customer.orders_sum_total_outstanding ?? 0).toFixed(2)}` },
            { term: 'Credit Enabled', 'description': customer.credit_enabled ? "YES" : "NO" },
            { term: 'Credit Balance', 'description': customer.credit_balance },
        ];

        if (customer.credit_enabled) {
            data.push({ term: 'Credit Expires At', 'description': customer.credit_expires_at });
        }

        return data.concat([
            { term: 'Credit Limit', 'description': customer.additional_data.credit_limit },
            { term: 'Discount', 'description': customer.additional_data.discount },
        ]);
    }

    return customer && (
        <Frame>
            <Page title={`Customer #${customer.account_number}`}
                  primaryAction={primaryAction}
                  secondaryActions={secondaryActions}
                  backAction={{ content: 'Go back', onAction: () => history.goBack() }}
            >
                <Layout>
                <CustomerOnHold customer={customer}/>
                <MergeModal show={showMergeModal} customer={customer} onClose={closeMergeModal} />
                <Layout.Section>
                    <LocationsCard customer={customer} placeOrder={placeOrder} />
                    <LegacyCard
                        title="Account Information"
                        sectioned
                        primaryFooterAction={primaryFooterAction}>
                        <DescriptionList items={[
                            { term: 'Shopify Customer ID', 'description': customer.shopify_customer_id },
                            { term: 'Account Number', 'description': customer.account_number },
                            { term: 'Email', 'description': customer.email },
                            { term: 'Phone', 'description': customer.phone_number },
                            { term: 'Name', 'description': `${customer.additional_data.first_name ?? ''} ${customer.additional_data.last_name ?? ''}` },
                            { term: 'Default Shipping Method', 'description': shippingMethod },
                            { term: 'Payment terms', 'description': customer?.payment_terms_template?.name ?? ''},
                        ]} />
                        <Text variant="headingMd" as="h2">Financial Information</Text>
                        <DescriptionList items={getCustomerFinancialInformation()} />
                    </LegacyCard>
                    <CustomerOrders customer={customer}/>
                    <EmailsSection customer={customer} setCustomer={setCustomer} />
                </Layout.Section>
                <Layout.Section variant="oneThird">
                    <ChangeStatus customer={customer} setCustomer={setCustomer} backUrl={backUrl} />
                    <OnHoldToggle customer={customer} setCustomer={setCustomer} />
                    <CustomerCreditCard
                        customer={customer}
                        onUpdate={onCustomerUpdate}
                        type="customer"
                        card={customer.card}
                    />
                    <LegacyCard sectioned title="Notes">
                        <div>{customer.additional_data.notes}</div>
                    </LegacyCard>

                    <Invoices />
                </Layout.Section>
            </Layout>
            </Page>
        </Frame>
    );
}

export default ShowCustomer;
